/**
 * Start Test Run Dialog
 *
 * Dialog for starting a workflow test run. The operator picks:
 *   - Scope: all eligible/promoted workflows OR by tag
 *   - Target (optional): a property and one of its environments
 *
 * When a property is selected, the suite filter only includes workflows
 * whose credential references all resolve to that property (workflows
 * with zero resolvable credentials or credentials bound elsewhere are
 * skipped with a reason in the run summary).
 *
 * @module StartTestRunDialog
 * @see docs/reference/test-runs.mdx
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  Button,
  Text,
  Select,
  Banner,
  Checkbox,
} from '@chatsdet/ui';
import { Play } from '@phosphor-icons/react';
import { useTagsQuery, type Tag } from '../../../hooks/useTags';
import { useTestSuite, useTestSuites } from '../../../features/test-suites';
import type { EligibleWorkflowSummary } from '../../../hooks/useWorkflows';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel';
import { useAuth } from '../../../contexts/AuthContext';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

type TestConfigEnvironment = {
  id: string;
  name: string;
  url: string;
  isDefault: boolean;
};

type TestConfigProperty = {
  id: string;
  name: string;
  baseUrl: string;
  environments: TestConfigEnvironment[];
};

type StartTestRunOptions = {
  tag?: string;
  /** When set, run this saved suite (layered execution) instead of a property/tag scan. */
  suiteId?: string;
  propertyId?: string;
  environmentId?: string;
  /**
   * Suite env mode: 'mixed' (each flow on its preferred env) or 'force' (pin the
   * whole suite to `environmentId`). Defaults to 'mixed' for suite runs.
   */
  environmentMode?: 'mixed' | 'force';
  /**
   * Clear `degraded` health on the in-scope flows before running, so this run
   * covers them instead of recording terminal degraded/not-run results.
   */
  reEnableDegraded?: boolean;
};

type StartTestRunDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onStart: (options: StartTestRunOptions) => void;
  isPending: boolean;
  activeRunCount?: number;
  promotedWorkflows?: EligibleWorkflowSummary[];
  isPromotedWorkflowPreviewLoading?: boolean;
};

const ANY_PROPERTY = '__any__';

type RunnableWorkflowWord = 'eligible' | 'promoted';

export function getStartTestRunWorkflowWord(isEligibilityModel: boolean): RunnableWorkflowWord {
  return isEligibilityModel ? 'eligible' : 'promoted';
}

export function getStartTestRunDialogCopy(isEligibilityModel: boolean) {
  const workflowWord = getStartTestRunWorkflowWord(isEligibilityModel);
  return {
    workflowWord,
    description: `Run ${workflowWord} workflows as regression tests`,
    allWorkflowsLabel: `All ${workflowWord} workflows`,
    tagPreviewPrompt: `Select a tag to preview how many ${workflowWord} flows will run.`,
  };
}

function formatFlowCount(count: number, workflowWord: RunnableWorkflowWord): string {
  return `${count} ${workflowWord} flow${count === 1 ? '' : 's'}`;
}

function pickDefaultEnvironmentId(envs: TestConfigEnvironment[]): string | null {
  if (envs.length === 0) return null;
  const def = envs.find((e) => e.isDefault);
  return (def ?? envs[0]).id;
}

export function StartTestRunDialog({
  isOpen,
  onOpenChange,
  onStart,
  isPending,
  activeRunCount = 0,
  promotedWorkflows = [],
  isPromotedWorkflowPreviewLoading = false,
}: StartTestRunDialogProps) {
  const [mode, setMode] = React.useState<'all' | 'by-tag' | 'by-suite'>('all');
  const [selectedTagName, setSelectedTagName] = React.useState<string | null>(null);
  const [selectedSuiteId, setSelectedSuiteId] = React.useState<string | null>(null);
  // Suite env mode: each flow on its preferred env (mixed) vs pin to one env (force).
  const [suiteEnvMode, setSuiteEnvMode] = React.useState<'mixed' | 'force'>('mixed');
  const [propertyId, setPropertyId] = React.useState<string>(ANY_PROPERTY);
  const [environmentId, setEnvironmentId] = React.useState<string | null>(null);
  const [reEnableDegraded, setReEnableDegraded] = React.useState(false);
  const isEligibilityModel = useEligibilityModel();
  const dialogCopy = React.useMemo(
    () => getStartTestRunDialogCopy(isEligibilityModel),
    [isEligibilityModel]
  );

  const tagsQuery = useTagsQuery();
  const tags = (tagsQuery.data?.data ?? []) as Tag[];
  const tagItems = React.useMemo(() => tags.map((t) => ({ id: t.name, label: t.name })), [tags]);

  const suitesEnabled = !!useAuth().user?.features?.suiteBuilderV2;
  // The complete suite list includes each external runner's configuration. It
  // is only useful after this dialog opens, so do not make it part of Test
  // Runs' initial load.
  const suitesQuery = useTestSuites(suitesEnabled && isOpen);
  const suiteItems = React.useMemo(
    () =>
      (suitesQuery.data ?? []).map((s) => ({
        id: s.id,
        label: s.kind === 'external' ? `${s.name} (external)` : s.name,
      })),
    [suitesQuery.data]
  );
  const selectedSuite = React.useMemo(
    () => suitesQuery.data?.find((s) => s.id === selectedSuiteId) ?? null,
    [selectedSuiteId, suitesQuery.data]
  );
  const selectedSuiteQuery = useTestSuite(
    mode === 'by-suite' && selectedSuite?.kind === 'native'
      ? (selectedSuiteId ?? undefined)
      : undefined
  );
  const isExternalSuiteSelected = selectedSuite?.kind === 'external';
  const suiteDegradedMembers = React.useMemo(
    () =>
      selectedSuiteQuery.data?.members.filter(
        (member) => member.layerIndex !== null && member.health === 'degraded'
      ) ?? [],
    [selectedSuiteQuery.data]
  );

  const testConfigQuery = useQuery({
    queryKey: ['workflows', 'test-config'],
    enabled: isOpen,
    queryFn: async (): Promise<{ properties: TestConfigProperty[] }> => {
      const res = await fetch(`${API_URL}/workflows/test-config`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load properties');
      return res.json();
    },
  });

  const properties = testConfigQuery.data?.properties ?? [];

  const propertyItems = React.useMemo(
    () => [
      { id: ANY_PROPERTY, label: 'All applications' },
      ...properties.map((p) => ({ id: p.id, label: p.name })),
    ],
    [properties]
  );

  const selectedProperty = React.useMemo(
    () => properties.find((p) => p.id === propertyId) ?? null,
    [properties, propertyId]
  );

  const environmentItems = React.useMemo(
    () =>
      (selectedProperty?.environments ?? []).map((e) => ({
        id: e.id,
        label: e.isDefault ? `${e.name} (default)` : e.name,
      })),
    [selectedProperty]
  );

  const runPreviewText = React.useMemo(() => {
    if (mode === 'by-suite') {
      if (!selectedSuiteId) return 'Select a suite to preview how many tests will run.';
      if (isExternalSuiteSelected) {
        return `The external suite "${selectedSuite?.name ?? 'this suite'}" will run using its configured environment.`;
      }
      if (selectedSuiteQuery.isLoading) return 'Loading suite run preview...';
      const runnableCount =
        selectedSuiteQuery.data?.members.filter((member) => member.layerIndex !== null).length ??
        selectedSuite?.workflowCount ??
        0;
      const suiteName = selectedSuiteQuery.data?.name ?? selectedSuite?.name ?? 'this suite';
      const degradedCopy =
        suiteDegradedMembers.length === 0
          ? ''
          : reEnableDegraded
            ? `; ${suiteDegradedMembers.length} degraded will be re-enabled`
            : `; ${suiteDegradedMembers.length} degraded will be skipped`;
      return `${runnableCount} workflow${runnableCount === 1 ? '' : 's'} in "${suiteName}" selected${degradedCopy}`;
    }

    if (isPromotedWorkflowPreviewLoading) return 'Loading test run preview...';

    if (mode === 'by-tag') {
      if (!selectedTagName) return dialogCopy.tagPreviewPrompt;
      const taggedCount = promotedWorkflows.filter((workflow) =>
        workflow.tags?.some((tag) => tag.name === selectedTagName)
      ).length;
      return `${formatFlowCount(taggedCount, dialogCopy.workflowWord)} tagged "${selectedTagName}" selected to run`;
    }

    const totalRunnable = promotedWorkflows.length;
    if (propertyId !== ANY_PROPERTY) {
      const propertyName = selectedProperty?.name ?? 'this application';
      return `${formatFlowCount(totalRunnable, dialogCopy.workflowWord)} will be evaluated for ${propertyName}; ineligible flows may be skipped`;
    }

    return `${formatFlowCount(totalRunnable, dialogCopy.workflowWord)} selected to run`;
  }, [
    dialogCopy,
    isPromotedWorkflowPreviewLoading,
    mode,
    promotedWorkflows,
    propertyId,
    reEnableDegraded,
    selectedProperty?.name,
    selectedSuite,
    selectedSuiteId,
    selectedSuiteQuery.data,
    selectedSuiteQuery.isLoading,
    suiteDegradedMembers.length,
    isExternalSuiteSelected,
    selectedTagName,
  ]);

  // Degraded flows in the current scope — the ones a run records as terminal
  // "degraded — not run" unless the operator explicitly re-enables them.
  const degradedInScope = React.useMemo(() => {
    if (mode === 'by-suite') return isExternalSuiteSelected ? [] : suiteDegradedMembers;
    const degraded = promotedWorkflows.filter((workflow) => workflow.health === 'degraded');
    if (mode !== 'by-tag') return degraded;
    if (!selectedTagName) return [];
    return degraded.filter((workflow) =>
      workflow.tags?.some((tag) => tag.name === selectedTagName)
    );
  }, [isExternalSuiteSelected, mode, promotedWorkflows, selectedTagName, suiteDegradedMembers]);

  // Reset state when the dialog opens.
  React.useEffect(() => {
    if (isOpen) {
      setMode('all');
      setSelectedTagName(null);
      setSelectedSuiteId(null);
      setPropertyId(ANY_PROPERTY);
      setEnvironmentId(null);
      setReEnableDegraded(false);
    }
  }, [isOpen]);

  // Never carry the opt-in across a scope change — the checkbox may not even be
  // rendered for the new scope, and a hidden checked box is an invisible mutation.
  React.useEffect(() => {
    setReEnableDegraded(false);
  }, [mode, selectedTagName, selectedSuiteId]);

  // When the property changes, auto-pick the default env (or the only env).
  React.useEffect(() => {
    if (propertyId === ANY_PROPERTY) {
      setEnvironmentId(null);
      return;
    }
    if (!selectedProperty) return;
    setEnvironmentId(pickDefaultEnvironmentId(selectedProperty.environments));
  }, [propertyId, selectedProperty]);

  const handleSubmit = () => {
    if (mode === 'by-tag' && !selectedTagName) return;
    if (mode === 'by-suite') {
      if (!selectedSuiteId) return;
      if (isExternalSuiteSelected) {
        onStart({ suiteId: selectedSuiteId });
        return;
      }
      onStart({
        suiteId: selectedSuiteId,
        environmentMode: suiteEnvMode,
        reEnableDegraded: reEnableDegraded && degradedInScope.length > 0 ? true : undefined,
      });
      return;
    }
    onStart({
      tag: mode === 'by-tag' ? (selectedTagName ?? undefined) : undefined,
      propertyId: propertyId !== ANY_PROPERTY ? propertyId : undefined,
      environmentId: propertyId !== ANY_PROPERTY && environmentId ? environmentId : undefined,
      reEnableDegraded: reEnableDegraded && degradedInScope.length > 0 ? true : undefined,
    });
  };

  const isSubmitDisabled =
    isPending ||
    (mode === 'by-tag' && !selectedTagName) ||
    (mode === 'by-suite' && !selectedSuiteId) ||
    (mode !== 'by-suite' && propertyId !== ANY_PROPERTY && !environmentId);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <span style={{ display: 'none' }} />
      <Dialog title="Start Test Run" description={dialogCopy.description}>
        <DialogContent>
          <div className="space-y-4">
            {activeRunCount > 0 && (
              <Banner variant="warning">
                {activeRunCount} test run{activeRunCount === 1 ? '' : 's'} currently in progress.
                Starting another will run both concurrently.
              </Banner>
            )}
            <fieldset className="space-y-2">
              <Text as="span" size="sm" className="font-medium mb-2">
                Scope
              </Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="test-run-mode"
                  checked={mode === 'all'}
                  onChange={() => setMode('all')}
                  className="accent-primary"
                />
                <Text size="sm">{dialogCopy.allWorkflowsLabel}</Text>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="test-run-mode"
                  checked={mode === 'by-tag'}
                  onChange={() => setMode('by-tag')}
                  className="accent-primary"
                />
                <Text size="sm">By tag</Text>
              </label>
              {suitesEnabled && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="test-run-mode"
                    checked={mode === 'by-suite'}
                    onChange={() => setMode('by-suite')}
                    className="accent-primary"
                  />
                  <Text size="sm">Suite</Text>
                </label>
              )}
            </fieldset>
            <Text size="xs" color="muted" className="pl-6">
              {runPreviewText}
            </Text>

            {mode === 'by-tag' && (
              <Select
                label="Tag"
                placeholder="Select a tag..."
                items={tagItems}
                selectedKey={selectedTagName}
                onSelectionChange={(key) => setSelectedTagName(key ? String(key) : null)}
                isRequired
              />
            )}

            {mode === 'by-suite' && (
              <>
                <Select
                  label="Suite"
                  placeholder={suiteItems.length === 0 ? 'No suites yet' : 'Select a suite...'}
                  items={suiteItems}
                  selectedKey={selectedSuiteId}
                  onSelectionChange={(key) => setSelectedSuiteId(key ? String(key) : null)}
                  isDisabled={suiteItems.length === 0}
                  isRequired
                />
                {!isExternalSuiteSelected && (
                  <div className="space-y-1.5 rounded-md border border-border bg-card p-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="suite-env-mode"
                        checked={suiteEnvMode === 'mixed'}
                        onChange={() => setSuiteEnvMode('mixed')}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        Each flow&apos;s preferred env{' '}
                        <span className="text-muted-foreground">
                          — mixed; runs each flow on its default environment
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="suite-env-mode"
                        checked={suiteEnvMode === 'force'}
                        onChange={() => setSuiteEnvMode('force')}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        Force the suite&apos;s environment{' '}
                        <span className="text-muted-foreground">
                          — pins all flows to the suite&apos;s env; skips ineligible flows
                        </span>
                      </span>
                    </label>
                  </div>
                )}
              </>
            )}

            {/* Property/environment targeting applies to property + tag scans;
                a suite runs against its own configured environment. */}
            {mode !== 'by-suite' && (
              <div className="space-y-2">
                <Select
                  label="Application"
                  items={propertyItems}
                  selectedKey={propertyId}
                  onSelectionChange={(key) => setPropertyId(key ? String(key) : ANY_PROPERTY)}
                  isDisabled={testConfigQuery.isLoading}
                />
                {propertyId !== ANY_PROPERTY && (
                  <Text size="xs" color="muted">
                    Only workflows whose credentials are bound to this property will run. Others
                    appear as skipped in the run summary.
                  </Text>
                )}
                {propertyId !== ANY_PROPERTY && (
                  <Select
                    label="Environment"
                    placeholder={
                      environmentItems.length === 0
                        ? 'No environments configured'
                        : 'Select an environment...'
                    }
                    items={environmentItems}
                    selectedKey={environmentId}
                    onSelectionChange={(key) => setEnvironmentId(key ? String(key) : null)}
                    isDisabled={environmentItems.length === 0}
                    isRequired
                  />
                )}
              </div>
            )}

            {/* Degraded flows are skipped until explicitly re-enabled. This clears
                the current run scope in one deliberate operation. */}
            {degradedInScope.length > 0 && (
              <div className="rounded-md border border-border bg-card p-3">
                <Checkbox
                  isSelected={reEnableDegraded}
                  onChange={setReEnableDegraded}
                  label="Re-enable degraded flows"
                  description={`${degradedInScope.length} degraded flow${
                    degradedInScope.length === 1 ? ' is' : 's are'
                  } currently skipped. Clears their degraded status and runs them — they stay in future runs until they start failing repeatedly again.`}
                />
              </div>
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => onOpenChange(false)} isDisabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSubmit}
            isDisabled={isSubmitDisabled}
            iconLeft={<Play size={16} weight="fill" />}
          >
            {isPending ? 'Starting...' : 'Start Test Run'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
